import React, { useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { FinancialPlanData, LgcEditorGroup } from '../../types/financialPlan';
import { EDITOR_GROUPS } from '../../utils/lgcEditorGroups';
import LgcSectionContent from './LgcSectionContent';
import './LgcEditDrawer.css';

interface LgcEditDrawerProps {
  open: boolean;
  activeGroup: LgcEditorGroup;
  plan: FinancialPlanData;
  sourcePlan: FinancialPlanData;
  onClose: () => void;
  onSelectGroup: (group: LgcEditorGroup) => void;
  onUpdate: (patch: Partial<FinancialPlanData>) => void;
}

const LgcEditDrawer: React.FC<LgcEditDrawerProps> = ({
  open,
  activeGroup,
  plan,
  sourcePlan,
  onClose,
  onSelectGroup,
  onUpdate,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="lgc-edit-overlay" role="presentation" onClick={onClose}>
      <div
        className="lgc-edit-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lgc-drawer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="lgc-drawer-header">
          <h2 id="lgc-drawer-title" className="lgc-drawer-title">
            Edit plan
          </h2>
          <button type="button" className="lgc-drawer-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>
        <div className="lgc-drawer-body">
          {EDITOR_GROUPS.map((group) => {
            const expanded = activeGroup === group.id;
            return (
              <section key={group.id} className="lgc-drawer-group">
                <button
                  type="button"
                  className={`lgc-drawer-group-btn${expanded ? ' is-open' : ''}`}
                  aria-expanded={expanded}
                  onClick={() => onSelectGroup(group.id)}
                >
                  <span>{group.label}</span>
                  <ChevronDown size={14} aria-hidden />
                </button>
                {expanded && (
                  <div className="lgc-drawer-group-body">
                    <LgcSectionContent
                      group={group.id}
                      plan={plan}
                      sourcePlan={sourcePlan}
                      onUpdate={onUpdate}
                    />
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LgcEditDrawer;
